import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  contentUpdatesAtom,
  currentlyBusyAtom,
  replacementsAtom,
  workingTreeAtom,
} from './atoms'
import { useCallback } from 'react'
import {
  filterNodeForParams,
  doInBatches,
  convertStringToRegex,
} from 'shared/utils'
import { INode } from 'shared/types/tree'
import { NodeContentUpdate, ReplacementData } from './types'
import { replaceParamsForUpdate } from 'shared/utils/contentTransforms'
import { isValidJsonObj } from 'shared/utils/contentTransforms'
import { retrieveIds, useUpdateTreeNode } from '../TreeRequests'
import { JsonObject } from 'shared/types'

export function useUpdateFunc(doUpdate: boolean) {
  const updateRequest = useUpdateTreeNode()
  const { resetTree } = useAtomValue(workingTreeAtom)
  return useCallback(
    async (update: NodeContentUpdate) => {
      const params = getNodeUpdateParams(update)
      await updateRequest({ ...retrieveIds(update.node), ...params })
      if (doUpdate) {
        resetTree?.()
      }
    },
    [resetTree, doUpdate],
  )
}

export function useUpdateAllFunc() {
  const [busy, setBusy] = useAtom(currentlyBusyAtom)
  const { resetTree } = useAtomValue(workingTreeAtom)
  const replacementData = useAtomValue(contentUpdatesAtom)
  const updateFunc = useUpdateFunc(false)
  return useCallback(async () => {
    if (busy) return
    setBusy(true)
    await doInBatches(replacementData.included, updateFunc).then(resetTree)
    setBusy(false)
  }, [replacementData, updateFunc, resetTree])
}

type ObjectStringDiffs = {
  diffKey: string
  oldValue: string
  newValue: string
}

export function getUpdateDiffs(update: NodeContentUpdate): ObjectStringDiffs[] {
  const updateData = getNodeUpdateParams(update)
  const currentData = filterNodeForParams(update.node)

  return getStringDifferences(currentData, updateData as JsonObject)
}

export function getStringDifferences(
  obj1: JsonObject,
  obj2: JsonObject,
  prefix = '',
): ObjectStringDiffs[] {
  const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})])
  return Array.from(keys).reduce<ObjectStringDiffs[]>((acc, key) => {
    const oldValue = obj1?.[key]
    const newValue = obj2?.[key]
    const diffKey = prefix ? `${prefix}.${key}` : key

    if (typeof oldValue === 'string' && typeof newValue === 'string') {
      if (oldValue !== newValue) {
        return [...acc, { diffKey, oldValue, newValue }]
      }
    } else if (isValidJsonObj(oldValue) && isValidJsonObj(newValue)) {
      return [...acc, ...getStringDifferences(oldValue, newValue, diffKey)]
    }
    return acc
  }, [])
}

export function useGoogleSheetTransform() {
  const [, setReplacements] = useAtom(replacementsAtom)

  return useCallback(
    (data: string[][]) => {
      setReplacements((prev) => [
        ...prev,
        ...data.reduce<ReplacementData[]>(
          (acc, [existingString, replaceWith]) => {
            if (!existingString.trim()) {
              return acc
            }
            const existing =
              convertStringToRegex(existingString) ?? existingString
            if (
              prev.find((p) => {
                if (p.replaceWith !== replaceWith) return false
                if (
                  p.existing instanceof RegExp &&
                  existing instanceof RegExp
                ) {
                  return p.existing.source === existing.source
                }
                return p.existing === existing
              })
            ) {
              return acc
            }
            return [...acc, { existing, replaceWith }]
          },
          [],
        ),
      ])
    },
    [setReplacements],
  )
}

function getNodeUpdateParams({ node, replacements }: NodeContentUpdate) {
  return replacements.reduce<Partial<INode>>(
    (acc, { existing, replaceWith }) => {
      const contentTransform = (content: string) => {
        if (!existing) return content
        return content.replaceAll(existing, replaceWith)
      }
      return replaceParamsForUpdate({
        searchString: existing,
        existingParams: acc,
        contentTransform,
      })
    },
    filterNodeForParams(node),
  )
}

export function useReplacementsAdd() {
  const replacementsSetter = useSetAtom(replacementsAtom)

  return useCallback(
    ({
      existing,
      replaceWith,
      caseSensitive,
    }: {
      existing: string | RegExp
      replaceWith: string
      caseSensitive: boolean
    }) => {
      if (!existing) return
      if (typeof existing === 'string' && !caseSensitive) {
        const converted = new RegExp(existing, 'gi')
        replacementsSetter((prev) => [
          ...prev,
          { existing: converted, replaceWith },
        ])
      } else {
        replacementsSetter((prev) => [...prev, { existing, replaceWith }])
      }
    },
    [replacementsSetter],
  )
}
